import {ClientType, getStoreName} from '@buncha/common/types/common';
import {H2Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import {BottomButton} from '@buncha/components/composites/Button';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {Box} from '@buncha/components/core/view';
import {EventType} from '@buncha/config/analytics/type';
import {GroupedAssignedItems} from '../types';
import {Modules} from '@buncha/utils/types';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {StyleSheet} from 'react-native';
import {navigation} from '@buncha/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  navigateToAddScreen: (item?: GroupedAssignedItems) => void;
  item: GroupedAssignedItems | undefined;
  storeIndex: string | undefined;
  runId: number | undefined;
  orderId: number | undefined;
  storeId: number | undefined;
}

const AddBottomSheet = (props: Props) => {
  const {open, onClose, navigateToAddScreen, item} = props;
  const {title, body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const theme = useColorThemeType('main');
  const mortise = useFontTheme('mortise');
  const muli = useFontTheme('muli');

  const navigateToScanningScreen = useCallback(() => {
    Analytics.eventWithProps('Scan an Item', {
      type: EventType.cta,
      runId: props.runId,
      client: ClientType.meijer,
      storeType: getStoreName(String(props.storeId)),
    });
    onClose();
    if (props.storeIndex && props.orderId && props.runId)
      navigation.navigate(ShopperScreens.ScannedAddItemPage, {
        storeIndex: props.storeIndex,
        runId: props.runId,
        orderId: props.orderId,
        storeId: props.storeId,
        returnPage: navigation.getCurrenRouteName(),
      });
  }, [onClose, props.orderId, props.runId, props.storeId, props.storeIndex]);

  const navigateToCatalogueAdd = useCallback(() => {
    Analytics.eventWithProps('Search Catalogue', {
      type: EventType.cta,
      runId: props.runId,
      client: ClientType.meijer,
      storeType: getStoreName(String(props.storeId)),
    });
    onClose();
    if (item) return navigateToAddScreen(item);
    if (navigation.getCurrenRouteName() === B2BScreens.B2BCheckout)
      navigateToAddScreen(item);
  }, [item, navigateToAddScreen, onClose, props.runId, props.storeId]);

  return (
    <BottomSheet open={open} onClose={onClose}>
      <Box style={styles.parentBox}>
        <Box style={styles.textContainer}>
          <H2Text
            style={[
              styles.textAlignLeft,
              {
                fontFamily: mortise?.[700]?.normal,
                color: theme?.text?.blue?.[300],
              },
            ]}>
            {title?.additionalItem}
          </H2Text>
          <H6Text
            style={{
              fontFamily: muli?.[700]?.normal,
              color: theme?.text?.gray?.[100],
            }}>
            {body?.addItemToOrder}
          </H6Text>
        </Box>
        <BottomButton
          style={styles.bottomButton}
          containerStyle={styles.bottomButtonContainer}
          title={button?.scanItem}
          onPress={navigateToScanningScreen}
        />
        <BottomButton
          style={[styles.bottomButton]}
          containerStyle={styles.bottomButtonContainer}
          title={button?.searchCatalogue}
          borderColor={theme?.background?.green?.[100]}
          borderWidth={1}
          titleColor={theme?.background?.green?.[100]}
          bgColor={theme?.background?.white?.[100]}
          onPress={navigateToCatalogueAdd}
        />
      </Box>
    </BottomSheet>
  );
};

export default React.memo(AddBottomSheet);

export const styles = StyleSheet.create({
  parentBox: {
    paddingVertical: 15,
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    paddingHorizontal: 15,
    gap: 20,
  },
  bottomButtonContainer: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  bottomButton: {
    width: '100%',
  },
  textAlignLeft: {textAlign: 'left'},
  textContainer: {
    gap: 10,
  },
});
